import { useEffect, useState, useMemo, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import InfinityScroll from 'src/components/InfinityScroll';
import FeedFilter from 'src/widgets/FeedFilter';
import DesktopFeedFilter from 'src/widgets/FeedFilter/Desktop';
// import ProjectsTabs from 'src/widgets/ProjectsTabs';
import { LayoutPaper, DesktopLayout } from 'src/layout';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useMessageStore from 'src/stores/messages';
import useFilterStore from 'src/stores/filter';
import { FILTER_IDS_MESSAGE_DATE } from 'src/widgets/FeedFilter/utils';
import { ROUTES_URL } from 'src/constants/routes';

import Title from 'src/components/Title';

import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Slide from '@mui/material/Slide';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';

import useUserStore from 'src/stores/user';
import LeadStreamsService from 'src/services/LeadStreamsService';
import { formatLeadStreamSourceName, resolveLeadStreamId } from 'src/utils/leadStreams';

import Card from './components/Card';
import ArticlesGroup from './components/ArticlesGroup';
import useBufferedFeed from './useBufferedFeed';
import NewLeadsButton from './components/NewLeadsButton';
import PaymentReturnNotice from './PaymentReturnNotice';
import FeedEmptyState from './FeedEmptyState';

import type { IMessage } from 'src/types/Message';

const FeedLoadingSkeleton = () => (
    <Stack spacing={2}>
        <Skeleton
            height={18}
            sx={{ borderRadius: 1, maxWidth: 120 }}
            variant="rounded"
        />
        {[0, 1, 2].map((item) => (
            <Box
                key={item}
                sx={{
                    bgcolor: 'var(--lm-surface)',
                    border: '1px solid var(--lm-border)',
                    borderRadius: 2,
                    p: 2,
                }}
            >
                <Stack spacing={1.25}>
                    <Skeleton
                        height={18}
                        sx={{ borderRadius: 1, maxWidth: 180 }}
                        variant="rounded"
                    />
                    <Skeleton
                        height={16}
                        sx={{ borderRadius: 1 }}
                        variant="rounded"
                        width="92%"
                    />
                    <Skeleton
                        height={16}
                        sx={{ borderRadius: 1 }}
                        variant="rounded"
                        width="76%"
                    />
                    <Stack
                        direction="row"
                        spacing={1}
                    >
                        <Skeleton
                            height={26}
                            sx={{ borderRadius: 999 }}
                            variant="rounded"
                            width={92}
                        />
                        <Skeleton
                            height={26}
                            sx={{ borderRadius: 999 }}
                            variant="rounded"
                            width={132}
                        />
                    </Stack>
                </Stack>
            </Box>
        ))}
    </Stack>
);

const Feed = () => {
    const { messages, clear } = useMessageStore(({ messages, clear }) => ({
        messages,
        clear,
    }));

    const location = useLocation();
    const { pathname, search } = location;
    const history = useHistory();
    const scopedStream = new URLSearchParams(search).get('stream');
    const trainingMode = new URLSearchParams(search).get('training') === '1' && Boolean(scopedStream && /^\d+$/.test(scopedStream));
    const requestedLead = new URLSearchParams(search).get('lead_id');
    const scopedLead = requestedLead && /^\d+$/.test(requestedLead) ? requestedLead : undefined;
    const {
        date = [],
        dateFrom: filterDateFrom,
        dateTo: filterDateTo,
        streams,
        leadFeedback,
        clearFilter,
    } = useFilterStore(({ date, dateFrom, dateTo, streams, leadFeedback, clearFilter }) => ({
        date,
        dateFrom,
        dateTo,
        streams,
        leadFeedback,
        clearFilter,
    }));

    const isMobile = useIsMobileSize();

    // Состояние выбранных сообщений
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isFavoriteMode, setIsFavoriteMode] = useState(false);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('from') !== 'companion') return;
        setIsFavoriteMode(false);
        // Consume the entry action so Back does not reopen it.
        params.delete('from');
        history.replace({ ...location, search: params.toString() ? `?${params}` : '' });
    }, [history, location]);
    const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set()); // Set ID удаляемых сообщений

    const isFavoriteOnly = isFavoriteMode;
    const selectedDateFilter = date[0] || FILTER_IDS_MESSAGE_DATE.ALL;
    const normalizedStreamIds = useMemo(
        () => (scopedStream && /^\d+$/.test(scopedStream) ? [scopedStream] : (streams || []).map(String).sort()),
        [streams, scopedStream]
    );
    useEffect(() => {
        if (trainingMode) {
            clearFilter();
            setIsFavoriteMode(false);
        }
    }, [trainingMode, clearFilter]);

    const current_subscription = useUserStore(({ current_subscription }) => current_subscription);
    const isBusiness = current_subscription?.plan?.name === 'Бизнес';
    const {
        data: leadStreams = [],
        isLoading: streamsLoading,
        isError: streamsError,
        refetch: refreshStreams,
    } = useQuery({
        queryKey: LeadStreamsService.queryKey,
        queryFn: LeadStreamsService.getStreams,
        cacheTime: 5 * 60 * 1000,
        keepPreviousData: true,
        onError: () => {},
        staleTime: 60 * 1000,
    });

    // Мемоизируем filterSnapshot для предотвращения лишних запросов
    const filterSnapshot = useMemo(
        () =>
            JSON.stringify({
                date: selectedDateFilter,
                dateFrom: selectedDateFilter === FILTER_IDS_MESSAGE_DATE.CUSTOM ? filterDateFrom : undefined,
                dateTo: selectedDateFilter === FILTER_IDS_MESSAGE_DATE.CUSTOM ? filterDateTo : undefined,
                streams: normalizedStreamIds,
                leadId: scopedLead,
                source: 'stream',
                isFavoriteOnly,
                leadFeedback,
            }),
        [selectedDateFilter, filterDateFrom, filterDateTo, normalizedStreamIds, scopedLead, isFavoriteOnly, leadFeedback]
    );

    // Мемоизируем filteredMessages вместо useState + useEffect
    const filteredMessages = useMemo(() => {
        let streamMessages = messages
            .filter((message) => message.source === 'stream')
            .map((message) => ({
                ...message,
                stream_id: message.stream_id ?? resolveLeadStreamId(message.stream_name, leadStreams),
                stream_display_name: formatLeadStreamSourceName(message.stream_name, leadStreams),
            }));

        if (!scopedLead && leadFeedback !== 'all') {
            streamMessages = streamMessages.filter((message) => message.feedback === leadFeedback);
        }

        if (!scopedLead && isFavoriteOnly) {
            return streamMessages.filter((message) => message.favorite);
        }
        return streamMessages;
    }, [leadFeedback, leadStreams, messages, isFavoriteOnly, scopedLead]);

    const handleSelect = useCallback((id: number, checked: boolean) => {
        setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
    }, []);

    const handleExport = useCallback(
        async (type: 'csv' | 'xlsx') => {
            if (selectedIds.length === 0) {
                setSnackbarOpen(true);
                return;
            }
            const XLSX = await import('xlsx');
            const exportMessages = filteredMessages.filter((msg) => selectedIds.includes(msg.id));
            const data = exportMessages.map((msg) => ({
                ID: msg.id,
                Дата: msg.date,
                Автор: msg.author,
                Сообщение: msg.content,
                Ссылка: msg.link,
                Чат: msg.chat_name,
            }));
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Избранное');
            if (type === 'csv') {
                XLSX.writeFile(wb, 'favorites.csv', { bookType: 'csv' });
            } else {
                XLSX.writeFile(wb, 'favorites.xlsx', { bookType: 'xlsx' });
            }
        },
        [selectedIds, filteredMessages]
    );

    // Мемоизируем вычисление дат
    const dateFrom = useMemo(() => {
        if (selectedDateFilter === FILTER_IDS_MESSAGE_DATE.ALL) {
            return undefined;
        }

        if (selectedDateFilter === FILTER_IDS_MESSAGE_DATE.CUSTOM) {
            return filterDateFrom;
        }

        if (selectedDateFilter === FILTER_IDS_MESSAGE_DATE.WEEK) {
            return dayjs().startOf('week').format('YYYY-MM-DD');
        }

        if (selectedDateFilter === FILTER_IDS_MESSAGE_DATE.MONTH) {
            return dayjs().startOf('month').format('YYYY-MM-DD');
        }
        return undefined;
    }, [selectedDateFilter, filterDateFrom]);

    const dateTo = useMemo(() => {
        if (selectedDateFilter === FILTER_IDS_MESSAGE_DATE.ALL) {
            return undefined;
        }

        if (selectedDateFilter === FILTER_IDS_MESSAGE_DATE.CUSTOM) {
            return filterDateTo;
        }

        if (selectedDateFilter === FILTER_IDS_MESSAGE_DATE.WEEK) {
            return dayjs().endOf('week').format('YYYY-MM-DD');
        }

        if (selectedDateFilter === FILTER_IDS_MESSAGE_DATE.MONTH) {
            return dayjs().endOf('month').format('YYYY-MM-DD');
        }
        return undefined;
    }, [selectedDateFilter, filterDateTo]);

    useEffect(() => {
        return () => {
            clear();
            clearFilter();
        };
    }, []);

    const {
        isLoading: isLoadingMessage,
        isError: messageError,
        refetch: refreshMessages,
        isFetching: isFetchingMessage,
        fetchNextPage,
        hasNextPage,
        hasNew,
        isApplying,
        showNew,
    } = useBufferedFeed(
        filterSnapshot,
        {
            source: 'stream',
            streams: normalizedStreamIds.join(','),
            lead_id: scopedLead,
            dateFrom,
            dateTo,
            is_favorite: isFavoriteOnly,
            feedback: leadFeedback === 'all' ? undefined : leadFeedback,
        },
        deletingIds.size > 0
    );

    const handleShowNew = async () => {
        const firstId = await showNew();
        if (firstId === undefined) return;
        requestAnimationFrame(() => {
            const first = document.querySelector<HTMLElement>(`[data-lead-id="${firstId}"]`);
            first?.scrollIntoView({ block: 'center', behavior: 'auto' });
            first?.focus({ preventScroll: true });
        });
    };

    // Мемоизируем группировку сообщений по датам
    const groupedMessages = useMemo(() => {
        return filteredMessages.reduce((acc, message) => {
            const dateKey = dayjs(message.date).format('DD.MM.YYYY');

            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }

            acc[dateKey].push(message);

            return acc;
        }, {} as Record<string, IMessage[]>);
    }, [filteredMessages]);

    const renderGroups = () => {
        if (isFavoriteOnly) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    gap="1rem"
                >
                    {filteredMessages.map((message) => (
                        <Card
                            key={message.id}
                            body={message}
                            trainingMode={trainingMode}
                            checkbox={
                                <Checkbox
                                    checked={selectedIds.includes(message.id)}
                                    onChange={(e) => handleSelect(message.id, e.target.checked)}
                                    color="primary"
                                />
                            }
                            onDeletingStart={(id) => setDeletingIds((prev) => new Set(prev).add(id))}
                            onDeletingEnd={(id) =>
                                setDeletingIds((prev) => {
                                    const next = new Set(prev);
                                    next.delete(id);
                                    return next;
                                })
                            }
                        />
                    ))}
                </Box>
            );
        }

        const keys = Object.keys(groupedMessages);

        return keys.map((key) => (
            <ArticlesGroup
                key={key}
                groupDate={key}
                list={groupedMessages[key]}
                trainingMode={trainingMode}
                onDeletingStart={(id) => setDeletingIds((prev) => new Set(prev).add(id))}
                onDeletingEnd={(id) =>
                    setDeletingIds((prev) => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                    })
                }
            />
        ));
    };

    const renderContent = () => {
        return (
            <>
                <PaymentReturnNotice />
                {/* Заголовок для главной страницы */}
                {pathname === ROUTES_URL.FEED && (
                    <Box
                        mb={20 / 8}
                        sx={{
                            position: 'sticky',
                            top: isMobile ? 48 : 0,
                            zIndex: 10,
                            bgcolor: 'var(--lm-canvas)',
                            py: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                        }}
                    >
                        <Title>Лиды</Title>
                        <NewLeadsButton
                            visible={hasNew}
                            loading={isApplying}
                            onClick={() => {
                                void handleShowNew();
                            }}
                        />
                    </Box>
                )}

                {trainingMode && (
                    <Box sx={{ mb: 2.5, px: 2, py: 1.5, bgcolor: 'var(--lm-accent-soft)', border: '1px solid var(--lm-border)', borderRadius: '8px' }}>
                        <Typography sx={{ fontWeight: 700, color: 'var(--lm-text)' }}>Обучение ИИ на реальных лидах</Typography>
                        <Typography sx={{ fontSize: 14, color: 'var(--lm-muted)', mt: 0.25 }}>
                            Показаны лиды выбранного потока. Нажмите «Да», если лид подходит, или «Нет», если не подходит.
                        </Typography>
                    </Box>
                )}

                {/* <Box
                   mx={-2}
                   style={{
                       marginTop: -2,
                   }}
                   bgcolor="background.default"
               >
                   {
                       isMobile && (
                           <ProjectsTabs
                               onChange={(id) => { }}
                               selectedIndex={0}
                               chatsList={[]}
                           />
                       )
                   }
               </Box> */}
                {isMobile && pathname === ROUTES_URL.FEED && (
                    <FeedFilter
                        isFavoriteMode={isFavoriteMode}
                        onFavoriteModeChange={setIsFavoriteMode}
                    />
                )}

                {isLoadingMessage ? (
                    <FeedLoadingSkeleton />
                ) : (
                    <>
                        {filteredMessages.length < 1 && (
                            <FeedEmptyState
                                streams={leadStreams}
                                loading={streamsLoading}
                                error={messageError || streamsError}
                                filtered={
                                    isFavoriteOnly ||
                                    selectedDateFilter !== FILTER_IDS_MESSAGE_DATE.ALL ||
                                    normalizedStreamIds.length > 0 ||
                                    leadFeedback !== 'all'
                                }
                                onRetry={() => {
                                    void refreshMessages();
                                    void refreshStreams();
                                }}
                                onReset={() => {
                                    clearFilter();
                                    setIsFavoriteMode(false);
                                    if (scopedStream) {
                                        const params = new URLSearchParams(search);
                                        params.delete('stream');
                                        history.replace(pathname + (params.toString() ? '?' + params.toString() : ''));
                                    }
                                }}
                            />
                        )}

                        {filteredMessages.length > 0 && (
                            <Box mb={2}>
                                <InfinityScroll
                                    isLoading={isFetchingMessage || deletingIds.size > 0}
                                    fetchNext={fetchNextPage}
                                    hasNextPage={!!hasNextPage && deletingIds.size === 0}
                                >
                                    {renderGroups()}
                                </InfinityScroll>
                            </Box>
                        )}
                    </>
                )}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={2500}
                    onClose={() => setSnackbarOpen(false)}
                    message="Выберите сообщения для экспорта"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                />
            </>
        );
    };

    if (!isMobile) {
        return (
            <LoadingWrapper isLoading={false}>
                {isFavoriteOnly && selectedIds.length > 0 && (
                    <Slide
                        direction="up"
                        in={selectedIds.length > 0}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Paper
                            elevation={8}
                            sx={{
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1301,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 3,
                                py: 2,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Typography variant="body1">
                                Выбрано {selectedIds.length} из {filteredMessages.length} сообщений
                            </Typography>
                            <Box
                                display="flex"
                                gap={1}
                            >
                                <Button
                                    variant="text"
                                    color="primary"
                                    onClick={() => setSelectedIds(filteredMessages.map((m) => m.id))}
                                    disabled={selectedIds.length === filteredMessages.length}
                                >
                                    Выбрать все
                                </Button>
                                <Button
                                    variant="text"
                                    color="secondary"
                                    onClick={() => setSelectedIds([])}
                                    disabled={selectedIds.length === 0}
                                >
                                    Очистить выбор
                                </Button>
                                <Tooltip
                                    title={!isBusiness ? 'Функция экспорта доступна только для тарифов: "Бизнес"' : ''}
                                    arrow
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleExport('csv')}
                                            disabled={!isBusiness}
                                        >
                                            Экспорт CSV
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip
                                    title={!isBusiness ? 'Функция экспорта доступна только для тарифов: "Бизнес"' : ''}
                                    arrow
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleExport('xlsx')}
                                            disabled={!isBusiness}
                                        >
                                            Экспорт Excel
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Paper>
                    </Slide>
                )}
                <DesktopLayout
                    rightSidebar={
                        <DesktopFeedFilter
                            isFavoriteMode={isFavoriteMode}
                            onFavoriteModeChange={setIsFavoriteMode}
                        />
                    }
                >
                    {renderContent()}
                </DesktopLayout>
            </LoadingWrapper>
        );
    }

    return (
        <LoadingWrapper isLoading={false}>
            {isFavoriteOnly && selectedIds.length > 0 && (
                <Slide
                    direction="up"
                    in={selectedIds.length > 0}
                    mountOnEnter
                    unmountOnExit
                >
                    <Paper
                        elevation={8}
                        sx={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1301,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 3,
                            py: 2,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Typography variant="body1">
                            Выбрано {selectedIds.length} из {filteredMessages.length} сообщений
                        </Typography>
                        <Box
                            display="flex"
                            gap={1}
                        >
                            <Button
                                variant="text"
                                color="primary"
                                onClick={() => setSelectedIds(filteredMessages.map((m) => m.id))}
                                disabled={selectedIds.length === filteredMessages.length}
                            >
                                Выбрать все
                            </Button>
                            <Button
                                variant="text"
                                color="secondary"
                                onClick={() => setSelectedIds([])}
                                disabled={selectedIds.length === 0}
                            >
                                Очистить выбор
                            </Button>
                            <Tooltip
                                title={!isBusiness ? 'Функция экспорта доступна только для тарифов: "Бизнес"' : ''}
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleExport('csv')}
                                        disabled={!isBusiness}
                                    >
                                        Экспорт CSV
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={!isBusiness ? 'Функция экспорта доступна только для тарифов: "Бизнес"' : ''}
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleExport('xlsx')}
                                        disabled={!isBusiness}
                                    >
                                        Экспорт Excel
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Paper>
                </Slide>
            )}
            <LayoutPaper isWhite>{renderContent()}</LayoutPaper>
        </LoadingWrapper>
    );
};

export default Feed;
